'use client';

import { ConfigurationDetailsView, type ConfigurationDetailsTotals } from '@slims/ui/organisms';
import { api, ApiError, apiFetch } from '@/lib/utils/api';
import { useParams, useRouter } from '@/lib/next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MqttLiveSection } from '@/components/admin/mqtt-live-section';
import { useScopeTree } from '@/hooks/use-scope-tree';

/**
 * La posizione di ripiego: Roma.
 *
 * Non pretende di essere giusta — pretende di essere **riconoscibile**. Chi apre
 * la scheda di una CER lombarda e legge 41,90 / 12,49 sa che quel campo non è
 * mai stato compilato; una posizione dedotta dal centro della macro-zona
 * tariffaria, che è ciò che il sistema usava prima, sarebbe stata plausibile e
 * quindi invisibile.
 */
const ROMA = { lat: '41.9', lon: '12.49' };

const NUMERIC = new Set(['pnrrGrantPct']);

type Form = Record<string, string>;

/**
 * Configuration "Dettagli" form: everything tied to the energy plant and the
 * cabina primaria / GSE setup. Fiscal and denomination data live on
 * the CER Dettagli form instead.
 */
export default function ConfigurationDetailsPage() {
	const { id } = useParams<{ id: string }>();
	const router = useRouter();
	const { refetch: refetchScopeTree } = useScopeTree();
	const [nome, setNome] = useState('');
	const [form, setForm] = useState<Form>({});
	// Separati dal form (che contiene solo stringhe): flag on/off booleani.
	const [disabled, setDisabled] = useState(false);
	const [demo, setDemo] = useState(false);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [loadErrorKind, setLoadErrorKind] = useState<'forbidden' | 'notFound' | undefined>();
	const [totals, setTotals] = useState<ConfigurationDetailsTotals | null>(null);
	const [saving, setSaving] = useState(false);
	const [geocoding, setGeocoding] = useState(false);
	const [zonaDaComune, setZonaDaComune] = useState<{
		zona: 'nord' | 'centro' | 'sud';
		regione: string;
	} | null>(null);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const v = (k: string) => form[k] ?? '';
	const set = (k: string, val: string) => setForm((f) => ({ ...f, [k]: val }));

	useEffect(() => {
		if (!id) return;
		let cancelled = false;
		setLoading(true);
		setLoadError(false);
		api
			.get<Record<string, unknown> & ConfigurationDetailsTotals>(`/api/admin/configs/${id}`)
			.then((d) => {
				if (cancelled) return;
				const str = (x: unknown) => (x == null ? '' : String(x));
				setNome(str(d.nome));
				setTotals(d);
				setForm({
					comune: str(d.comune),
					lat: str(d.lat ?? ROMA.lat),
					lon: str(d.lon ?? ROMA.lon),
					cabinaPrimariaCodice: str(d.cabinaPrimariaCodice),
					areaConvenzionale: str(d.areaConvenzionale),
					zonaGeografica: str(d.zonaGeografica),
					codiceGSE: str(d.codiceGSE),
					dataAttivazioneGSE: str(d.dataAttivazioneGSE),
					pnrrGrantPct: str(d.pnrrGrantPct ?? 0),
				});
				setDisabled(!!d.disabled);
				setDemo(!!d.demo);
				setLoading(false);
			})
			.catch((err: unknown) => {
				if (cancelled) return;
				// 403/404 non sono guasti da riprovare (INB-352).
				const status = err instanceof ApiError ? err.status : 0;
				setLoadErrorKind(status === 403 ? 'forbidden' : status === 404 ? 'notFound' : undefined);
				setLoadError(true);
				setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [id]);

	/**
	 * Le due coordinate, validate prima di partire.
	 *
	 * Non stanno in `NUMERIC` perché `Number(val) || 0` — la regola degli altri
	 * campi — trasformerebbe un campo svuotato in `0`, cioè nel golfo di Guinea:
	 * un valore perfettamente valido per il database, che manderebbe la cache
	 * meteo a chiedere l'irraggiamento di un punto in mezzo all'oceano e la
	 * produzione stimata a ripiegare in silenzio sul profilo tipico.
	 */
	function coordErrors(): Record<string, string> {
		const out: Record<string, string> = {};
		for (const [k, max, nome] of [
			['lat', 90, 'latitudine'],
			['lon', 180, 'longitudine'],
		] as const) {
			const n = Number(v(k).replace(',', '.'));
			if (v(k).trim() === '') out[k] = `La ${nome} è obbligatoria (Roma: ${ROMA[k]}).`;
			else if (!Number.isFinite(n)) out[k] = `La ${nome} deve essere un numero.`;
			else if (n < -max || n > max) out[k] = `La ${nome} deve stare fra -${max} e ${max}.`;
		}
		return out;
	}

	async function fillFromComune() {
		const comune = v('comune').trim();
		if (!comune) {
			toast.error('Inserisci prima il comune sede');
			return;
		}
		setGeocoding(true);
		try {
			const geo = await api.get<{
				lat: number;
				lon: number;
				regione?: string | null;
				zonaGeografica?: 'nord' | 'centro' | 'sud' | null;
			}>(`/api/admin/geocode?comune=${encodeURIComponent(comune)}`);
			// La zona del FCzonale viene dalla regione del comune (INB-326): la si
			// propone, e se poi la si cambia il campo lo segnala.
			const zona = geo.zonaGeografica ?? null;
			setZonaDaComune(zona && geo.regione ? { zona, regione: geo.regione } : null);
			setForm((f) => ({
				...f,
				lat: String(geo.lat),
				lon: String(geo.lon),
				...(zona ? { zonaGeografica: zona } : {}),
			}));
			setErrors((e) => ({ ...e, lat: '', lon: '' }));
			toast.success(`Posizione di ${comune}: ${geo.lat.toFixed(4)}, ${geo.lon.toFixed(4)}`);
		} catch {
			toast.error(`Nessun comune italiano trovato per «${comune}»`);
		} finally {
			setGeocoding(false);
		}
	}

	async function handleSave() {
		const coordErr = coordErrors();
		setErrors(coordErr);
		if (Object.values(coordErr).some(Boolean)) {
			toast.error('La posizione per i dati meteo è obbligatoria');
			return;
		}
		setSaving(true);
		const body: Record<string, unknown> = { disabled, demo };
		for (const [k, val] of Object.entries(form)) {
			if (k === 'lat' || k === 'lon') body[k] = Number(val.replace(',', '.'));
			else body[k] = NUMERIC.has(k) ? Number(val) || 0 : val;
		}
		try {
			const res = await apiFetch(`/api/admin/configs/${id}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			});
			if (res.ok) {
				toast.success('Dettagli configurazione salvati');
				// L'area convenzionale è l'etichetta della config nei selettori:
				// riallinea l'albero, poi torna all'elenco.
				await refetchScopeTree();
				router.push('/configurations');
				return;
			}
			toast.error('Errore nel salvataggio');
		} catch {
			toast.error('Errore di rete');
		} finally {
			setSaving(false);
		}
	}

	return (
		<ConfigurationDetailsView
			nome={nome}
			form={form}
			totals={totals}
			loading={loading}
			loadError={loadError}
			loadErrorKind={loadErrorKind}
			saving={saving}
			geocoding={geocoding}
			zonaDaComune={zonaDaComune}
			disabled={disabled}
			demo={demo}
			errors={errors}
			onFieldChange={set}
			onDisabledChange={setDisabled}
			onDemoChange={setDemo}
			onSave={() => void handleSave()}
			onCancel={() => router.push('/configurations')}
			onGeocode={() => void fillFromComune()}
			mqttSection={id ? <MqttLiveSection orgId={id} /> : null}
		/>
	);
}
